import React, { useState, useEffect } from 'react';
import { Box, Typography, keyframes } from '@mui/material';

interface WindowsUpdateProps {
  duration?: number;
}

const WindowsUpdate = ({ duration = 60 }: WindowsUpdateProps) => {
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);

  const updatePhases = [
    { 
      message: "Working on updates", 
      subMessage: "Don't turn off your computer"
    },
    {
      message: "Installing updates",
      subMessage: "Your PC will restart several times"
    },
    {
      message: "Finalizing updates",
      subMessage: "Almost there, keep your PC on"
    }
  ];

  // Animációs keyframes
  const rotate = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const dotMovement = keyframes`
    0% { 
      transform: rotate(0deg) translateY(-50px) rotate(0deg);
      animation-timing-function: ease-out;
    }

    50% { 
      transform: rotate(180deg) translateY(-50px) rotate(-180deg);
      animation-timing-function: ease-in;
    }

    100% { 
      transform: rotate(360deg) translateY(-50px) rotate(-360deg);
      animation-timing-function: ease-out;
    }
  `;

  useEffect(() => {
    const increment = 100 / duration;
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        
        if (prev < 33) setCurrentPhase(0);
        else if (prev < 66) setCurrentPhase(1);
        else setCurrentPhase(2);

        return prev + increment;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [duration]);

  return (
    <Box
      sx={{
        backgroundColor: '#0078d7',
        color: 'white',
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        fontFamily: 'Segoe UI, sans-serif',
        textAlign: 'center',
      }}
    >
      {/* Töltő animáció */}
      <Box sx={{ position: 'relative', width: 120, height: 120, mb: 4 }}>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
        >
          {[...Array(4)].map((_, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                animation: `${dotMovement} 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                animationDelay: `${index * 0.2}s`,
                transformOrigin: 'left center'
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 0 8px rgba(255, 255, 255, 0.5)',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Szöveges tartalom */}
      <Typography variant="h4" sx={{ fontWeight: 500 }}>
        {updatePhases[currentPhase].message}
      </Typography>
      
      <Typography variant="h5" sx={{ mt: 2, opacity: 0.8 }}>
        {Math.round(progress)}% complete
      </Typography>

      <Typography variant="h5" sx={{ mt: 2, opacity: 0.7 }}>
        {updatePhases[currentPhase].subMessage}
      </Typography>
    </Box>
  );
};

export default WindowsUpdate;